"use client";

import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Upload, Grid3X3, FileText, Loader2 } from "lucide-react";
import { post } from "../../lib/api";
import { toast } from "sonner";
import { readFileWithEncodingDetection } from "../../lib/encoding-utils";

export const CrosswordPlanner = () => {
    const [fileContent, setFileContent] = useState("");
    const [grid, setGrid] = useState([]);
    const [gridNumbers, setGridNumbers] = useState([]);
    const [acrossClues, setAcrossClues] = useState([]);
    const [downClues, setDownClues] = useState([]);
    const [puzzleName, setPuzzleName] = useState("");
    const [uploadStatus, setUploadStatus] = useState(null);
    const [scheduledDate, setScheduledDate] = useState("");
    const [lastUploadedDate, setLastUploadedDate] = useState(null);

    useEffect(() => {
        const today = new Date();
        const todayString = today.toISOString().split("T")[0];
        setScheduledDate(todayString);
    }, []);

    useEffect(() => {
        if (lastUploadedDate) {
            const nextDay = new Date(lastUploadedDate);
            nextDay.setDate(nextDay.getDate() + 1);
            const nextDayString = nextDay.toISOString().split("T")[0];
            setScheduledDate(nextDayString);
        }
    }, [lastUploadedDate]);

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Formatting function to convert grid and gridNumbers to a single string
    const formatDatabaseString = (grid, gridNumbers) => {
        let letters = "";
        let numbers = "";

        for (let y = 0; y < grid.length; y++) {
            for (let x = 0; x < grid[y].length; x++) {
                const letter = grid[y][x] || " "; // handle null/undefined cells
                const number = gridNumbers[y][x] || "null"; // handle null/undefined cells

                if (y === 0 && x === 0) {
                    letters = letter;
                    numbers = number;
                } else {
                    letters += `,${letter}`;
                    numbers += `,${number}`;
                }
            }
        }

        return `${letters}-${numbers}`;
    };

    // Formatting function to convert clues array to a pipe-separated string
    const formatClues = (clues) => {
        return clues.map((clue) => `${clue.number}§${clue.length}§${clue.text}`).join("|");
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                const text = await readFileWithEncodingDetection(file);
                setFileContent(text);
                parseXMLFile(text);
                setPuzzleName(file.name.replace(/\.[^/.]+$/, "")); // Set puzzle name from filename
            } catch (error) {
                toast.error("Failed to read file. Please try again.");
                console.error("File reading error:", error);
            }
        }
    };

    const parseXMLFile = (content) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, "application/xml");

        const gridElement = xmlDoc.querySelector("grid");
        if (!gridElement) {
            toast.error("No grid element found in the XML.");
            return;
        }

        const width = Number.parseInt(gridElement.getAttribute("width"), 10);
        const height = Number.parseInt(gridElement.getAttribute("height"), 10);

        const puzzle = Array.from({ length: height }, () => Array(width).fill(" "));
        const puzzleNumbers = Array.from({ length: height }, () => Array(width).fill(null));

        const cluesAcross = [];
        const cluesDown = [];

        const cluesSections = xmlDoc.querySelectorAll("clues");
        cluesSections.forEach((section) => {
            const title = section.querySelector("title");
            if (title) {
                const category = title.textContent.trim().toLowerCase();
                const clues = section.querySelectorAll("clue");
                clues.forEach((clue) => {
                    const number = Number.parseInt(clue.getAttribute("number"), 10);
                    const length = clue.getAttribute("format");
                    const text = clue.textContent.trim();
                    if (category === "across" || category === "horizontaal:") {
                        cluesAcross.push({ number, text, length });
                    } else if (category === "down" || category === "verticaal:") {
                        cluesDown.push({ number, text, length });
                    }
                });
            }
        });

        const cells = xmlDoc.querySelectorAll("cell");
        cells.forEach((cell) => {
            const x = Number.parseInt(cell.getAttribute("x"), 10) - 1;
            const y = Number.parseInt(cell.getAttribute("y"), 10) - 1;
            const cellType = cell.getAttribute("type");
            const solution = cell.getAttribute("solution");
            const number = cell.getAttribute("number");

            if (cellType === "block") {
                puzzle[y][x] = "#";
                puzzleNumbers[y][x] = null;
            } else if (solution) {
                puzzle[y][x] = solution;
                if (number) {
                    const numberInt = Number.parseInt(number, 10);
                    puzzleNumbers[y][x] = numberInt < 10 ? `${number}` : number;
                } else {
                    puzzleNumbers[y][x] = null;
                }
            }
        });

        setGrid(puzzle);
        setGridNumbers(puzzleNumbers);
        setAcrossClues(cluesAcross);
        setDownClues(cluesDown);
        toast.success("Crossword XML parsed successfully!");
    };

    const handlePostCrossword = async () => {
        if (!puzzleName.trim()) {
            toast.error("Please enter a puzzle name.");
            return;
        }

        if (grid.length === 0) {
            toast.error("No puzzle to upload. Please upload and parse an XML file first.");
            return;
        }
        if (!scheduledDate) {
            toast.error("Please select a date for the challenge.");
            return;
        }

        setUploadStatus({ type: "loading", message: "Uploading puzzle..." });

        const database_string = formatDatabaseString(grid, gridNumbers);
        const down_clues_string = formatClues(downClues);
        const across_clues_string = formatClues(acrossClues);

        const challengeVariants = [
            { variant_id: 1, variant_name: "data", data: database_string },
            { variant_id: 2, variant_name: "down", data: down_clues_string },
            { variant_id: 3, variant_name: "across", data: across_clues_string },
        ];

        try {
            for (const variant of challengeVariants) {
                const response = await post({
                    method: "addChallenge",
                    game_id: 17,
                    challenge_id: puzzleName,
                    data: variant.data,
                    variant_id: variant.variant_id,
                    variant_name: variant.variant_name,
                }, "/challenges");

                if (response.error) {
                    toast.error(`Failed to upload variant '${variant.variant_name}': ${response.error}`);
                    setUploadStatus({ type: "error", message: `Failed to upload variant '${variant.variant_name}'` });
                    return;
                }
            }

            const dailyResponse = await post({
                method: "addDailyChallenge",
                game_id: 17,
                date: scheduledDate,
                daily_challenge_info: puzzleName,
            }, "/challenges");

            if (dailyResponse.error) {
                toast.error(`Challenge data uploaded but scheduling failed: ${dailyResponse.error}`);
                setUploadStatus({ type: "error", message: "Scheduling failed" });
                return;
            }

            toast.success(`Crossword "${puzzleName}" uploaded and scheduled for ${formatDateForDisplay(scheduledDate)}!`);
            setUploadStatus({ type: "success", message: "Puzzle uploaded successfully!" });

            setLastUploadedDate(scheduledDate);
            setPuzzleName("");
            setGrid([]);
            setGridNumbers([]);
            setAcrossClues([]);
            setDownClues([]);
            setFileContent("");

        } catch (error) {
            toast.error(error.message || "An unknown error occurred during upload.");
            setUploadStatus({ type: "error", message: error.message });
        }
    };


    return (
        <div className="space-y-8">
            {/* Upload Section */}
            {grid.length === 0 && (
                <div className="bg-[#ffffff] dark:bg-gray-800 border-2 border-dashed border-slate-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                    <Upload className="w-12 h-12 text-slate-400 dark:text-gray-500 mx-auto mb-4" />
                    <Label htmlFor="xmlFile" className="cursor-pointer">
                        <span className="text-lg font-medium text-slate-700 dark:text-gray-300">Choose XML file</span>
                        <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">Select your crossword XML file to get started</p>
                    </Label>
                    <Input id="xmlFile" type="file" accept=".xml" onChange={handleFileUpload} className="hidden" />
                </div>
            )}

            {/* Main Content Grid */}
            {grid.length > 0 && (
                <div className="grid lg:grid-cols-3 gap-0 border-1 border-slate-300 dark:border-gray-700 rounded-lg overflow-hidden">
                    {/* Crossword Grid */}
                    <div className="lg:col-span-2">
                        <div className="bg-[#ffffff] dark:bg-gray-800 overflow-hidden border-r dark:border-gray-700">
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-gray-700">
                                <Grid3X3 className="w-5 h-5 text-gray-800 dark:text-gray-300" />
                                <span className="font-bold text-lg text-gray-800 dark:text-gray-300">Crossword Grid</span>
                            </div>
                            <div className="p-4 flex justify-center dark:bg-gray-900/50">
                                <ScrollArea className="h-[600px]">
                                    <div className="inline-block border-2 border-slate-300 dark:border-gray-600 rounded-lg overflow-hidden shadow-inner bg-[#ffffff] dark:bg-gray-800">
                                        {grid.map((row, rowIndex) => (
                                            <div key={rowIndex} className="flex">
                                                {row.map((cell, cellIndex) => {
                                                    const isBlackCell = cell === "#";

                                                    return (
                                                        <div
                                                            key={cellIndex}
                                                            className={`
                                  w-11 h-11 border border-slate-300 dark:border-gray-600 flex items-center justify-center relative text-base font-mono
                                  ${isBlackCell ? "bg-slate-900 dark:bg-black" : "bg-[#ffffff] dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"}
                                `}
                                                        >
                                                            {gridNumbers[rowIndex][cellIndex] && !isBlackCell && (
                                                                <span className="absolute top-0.5 left-0.5 text-[10px] font-bold text-slate-600 dark:text-gray-400 leading-none">{gridNumbers[rowIndex][cellIndex]}</span>
                                                            )}
                                                            {!isBlackCell && <span className="text-base font-bold text-slate-800 dark:text-gray-200 mt-1">{cell !== " " ? cell : ""}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>
                    </div>

                    {/* Clues Section */}
                    <div className="space-y-0 bg-[#ffffff] dark:bg-gray-800">
                        {/* Across Clues */}
                        <div className="bg-[#ffffff] dark:bg-gray-800">
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-gray-700">
                                <FileText className="w-4 h-4 text-gray-800 dark:text-gray-300" />
                                <span className="font-bold text-lg text-gray-800 dark:text-gray-300">Across</span>
                                <Badge variant="secondary" className="ml-auto bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                    {acrossClues.length}
                                </Badge>
                            </div>
                            <div className="p-4">
                                <ScrollArea className="h-64">
                                    <div className="space-y-3">
                                        {acrossClues.map((clue, index) => (
                                            <div key={index} className="p-3 bg-slate-50 dark:bg-gray-900/50 rounded-lg border border-slate-200 dark:border-gray-700">
                                                <div className="flex items-start gap-2">
                                                    <Badge variant="outline" className="shrink-0 font-mono dark:border-gray-600 dark:text-gray-300">
                                                        {clue.number}
                                                    </Badge>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-slate-700 dark:text-gray-300 leading-relaxed">{clue.text}</p>
                                                        {clue.length && <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Length: {clue.length}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>

                        {/* Down Clues */}
                        <div className="bg-[#ffffff] dark:bg-gray-800">
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-t border-slate-200 dark:border-gray-700">
                                <FileText className="w-4 h-4 text-gray-800 dark:text-gray-300" />
                                <span className="font-bold text-lg text-gray-800 dark:text-gray-300">Down</span>
                                <Badge variant="secondary" className="ml-auto bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                    {downClues.length}
                                </Badge>
                            </div>
                            <div className="p-4">
                                <ScrollArea className="h-64">
                                    <div className="space-y-3">
                                        {downClues.map((clue, index) => (
                                            <div key={index} className="p-3 bg-slate-50 dark:bg-gray-900/50 rounded-lg border border-slate-200 dark:border-gray-700">
                                                <div className="flex items-start gap-2">
                                                    <Badge variant="outline" className="shrink-0 font-mono dark:border-gray-600 dark:text-gray-300">
                                                        {clue.number}
                                                    </Badge>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-slate-700 dark:text-gray-300 leading-relaxed">{clue.text}</p>
                                                        {clue.length && <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Length: {clue.length}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Puzzle Section */}
            {grid.length > 0 && (
                <div className="space-y-4 pt-4">
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-900 rounded-lg">
                        <Label htmlFor="puzzleName" className="text-sm font-medium text[#000000] dark:text-white">
                            Puzzle Name:
                        </Label>
                        <Input
                            id="puzzleName"
                            placeholder="Enter a memorable puzzle name"
                            value={puzzleName}
                            onChange={(e) => setPuzzleName(e.target.value)}
                            className="mt-1 transition-all focus:ring-2 focus:ring-primary/20 bg-[#ffffff] dark:bg-gray-800"
                        />
                    </div>

                    <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-900 rounded-lg">
                        <div className="space-y-3">
                            <Label htmlFor="scheduledDate" className="block text-sm font-medium text[#000000] dark:text-white">
                                Schedule Challenge Date:
                            </Label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="date"
                                    id="scheduledDate"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="max-w-xs text-gray-900 bg-[#ffffff] dark:text-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                                />
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {scheduledDate && `Will be scheduled for: ${formatDateForDisplay(scheduledDate)}`}
                                </div>
                            </div>
                            {lastUploadedDate && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Last upload was scheduled for {formatDateForDisplay(lastUploadedDate)}
                                </div>
                            )}
                        </div>
                    </div>

                    <Button onClick={handlePostCrossword} disabled={!puzzleName.trim() || grid.length === 0 || uploadStatus?.type === "loading"} className="w-full h-11 text-base font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 dark:disabled:text-gray-400">
                        {uploadStatus?.type === "loading" ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            `Schedule Puzzle for ${formatDateForDisplay(scheduledDate)}`
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CrosswordPlanner; 